import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import API_BASE_URL from "../config/apiConfig";
import { apiFetch, readJsonSafe } from "../config/apiClient";
import { getUser, isAuthenticated } from "../config/auth";
import {
  getQuizAttemptSummary,
  isQuizAttempted,
  makeCurrentAffairsAttemptKey,
  markQuizAttempted,
} from "../config/quizAttemptCache";
import "./currentAffairs.css";

const OG_IMAGE_URL = "https://www.aamschool.in/logo512.png";
const SITE_URL = "https://www.aamschool.in";
const buildCanonicalUrl = (path) => `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;

const slugify = (title = "") =>
  String(title)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const makeSlugWithId = (title, id) => `${slugify(title)}-${id}`;
const parseIdFromSlugWithId = (slugWithId) => {
  const slugText = String(slugWithId || "");
  const matched = slugText.match(/-(\d+)$/);
  if (!matched) return null;
  const parsed = Number(matched[1]);
  return Number.isFinite(parsed) ? parsed : null;
};
const redirectToLoginForCurrentAffairsQuiz = (navigate, redirectTo) => {
  navigate("/login", {
    replace: true,
    state: {
      loginMessage: "Please log in to open and attempt this daily current affairs quiz.",
      redirectTo,
    },
  });
};

const formatDuration = (value) => {
  const totalSeconds = Math.max(0, Number(value) || 0);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};
const QUIZ_SOLUTION_CACHE_PREFIX = "quiz_solution_v1::";
const makeSolutionCacheKey = (attemptKey) =>
  `${QUIZ_SOLUTION_CACHE_PREFIX}${String(attemptKey || "").trim().toLowerCase()}`;
const saveAttemptSolutions = (attemptKey, payload) => {
  if (!attemptKey || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(makeSolutionCacheKey(attemptKey), JSON.stringify(payload));
  } catch {
    // Ignore storage write failures.
  }
};
const readAttemptSolutions = (attemptKey) => {
  if (!attemptKey || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(makeSolutionCacheKey(attemptKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};
const translationCache = new Map();
const HINDI_QUIZ_CACHE_PREFIX = "ca_quiz_hi_v5::";
const fullyQuotedTextPattern = /^(["'])[\s\S]*\1$/;
const hindiGlossaryFixes = [
  [/correct option/gi, "सही विकल्प"],
  [/wrong option/gi, "गलत विकल्प"],
  [/current affairs/gi, "समसामयिकी"],
  [/quiz/gi, "प्रश्नोत्तरी"],
  [/question/gi, "प्रश्न"],
  [/options?/gi, "विकल्प"],
  [/description/gi, "विवरण"],
  [/details?/gi, "विवरण"],
  [/exam/gi, "परीक्षा"],
  [/initiative/gi, "पहल"],
  [/sustainable/gi, "सतत"],
  [/energy/gi, "ऊर्जा"],
  [/village/gi, "गांव"],
  [/state/gi, "राज्य"],
  [/government/gi, "सरकार"],
  [/launched/gi, "शुरू किया"],
  [/adoption/gi, "अपनाने"],
  [/rural areas?/gi, "ग्रामीण क्षेत्रों"],
  [/carbon footprint/gi, "कार्बन फुटप्रिंट"],
  [/development/gi, "विकास"],
];
const protectedHindiTerms = new Map([
  ["andhra pradesh", "आंध्र प्रदेश"],
  ["arunachal pradesh", "अरुणाचल प्रदेश"],
  ["assam", "असम"],
  ["bihar", "बिहार"],
  ["chhattisgarh", "छत्तीसगढ़"],
  ["goa", "गोवा"],
  ["gujarat", "गुजरात"],
  ["haryana", "हरियाणा"],
  ["himachal pradesh", "हिमाचल प्रदेश"],
  ["jharkhand", "झारखंड"],
  ["karnataka", "कर्नाटक"],
  ["kerala", "केरल"],
  ["madhya pradesh", "मध्य प्रदेश"],
  ["maharashtra", "महाराष्ट्र"],
  ["manipur", "मणिपुर"],
  ["meghalaya", "मेघालय"],
  ["mizoram", "मिजोरम"],
  ["nagaland", "नागालैंड"],
  ["odisha", "ओडिशा"],
  ["punjab", "पंजाब"],
  ["rajasthan", "राजस्थान"],
  ["sikkim", "सिक्किम"],
  ["tamil nadu", "तमिलनाडु"],
  ["telangana", "तेलंगाना"],
  ["tripura", "त्रिपुरा"],
  ["uttar pradesh", "उत्तर प्रदेश"],
  ["uttarakhand", "उत्तराखंड"],
  ["west bengal", "पश्चिम बंगाल"],
  ["state psc", "राज्य पीएससी"],
  ["delhi", "दिल्ली"],
  ["new delhi", "नई दिल्ली"],
  ["mumbai", "मुंबई"],
  ["bengaluru", "बेंगलुरु"],
  ["bangalore", "बेंगलुरु"],
  ["chennai", "चेन्नई"],
  ["hyderabad", "हैदराबाद"],
  ["kolkata", "कोलकाता"],
  ["pune", "पुणे"],
  ["ahmedabad", "अहमदाबाद"],
  ["indore", "इंदौर"],
  ["jaipur", "जयपुर"],
  ["lucknow", "लखनऊ"],
  ["bhopal", "भोपाल"],
  ["surat", "सूरत"],
  ["patna", "पटना"],
  ["chandigarh", "चंडीगढ़"],
  ["bhubaneswar", "भुवनेश्वर"],
  ["kochi", "कोच्चि"],
  ["thiruvananthapuram", "तिरुवनंतपुरम"],
  ["nagpur", "नागपुर"],
  ["varanasi", "वाराणसी"],
  ["agra", "\u0906\u0917\u0930\u093e"],
  ["kanpur", "कानपुर"],
  ["amritsar", "अमृतसर"],
  ["upsc", "यूपीएससी"],
  ["ssc", "एसएससी"],
  ["banking", "बैंकिंग"],
  ["railway", "रेलवे"],
]);

const normalizeLanguage = (lang) => (lang === "hi" ? "hi" : "en");

const cleanupHindiText = (text) =>
  String(text || "")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const applyHindiGlossaryFixes = (text) => {
  let output = cleanupHindiText(text);
  hindiGlossaryFixes.forEach(([pattern, replacement]) => {
    output = output.replace(pattern, replacement);
  });
  return output;
};

const getProtectedHindiTerm = (text) => {
  const key = String(text || "").trim().toLowerCase();
  return protectedHindiTerms.get(key) || null;
};

const escapeRegExp = (value) =>
  String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const makePlaceholderToken = (prefix, index) =>
  `${String.fromCharCode(0xe000)}${prefix}${index}${String.fromCharCode(0xe001)}`;

const replacePlaceholderVariants = (text, { token, index }, replacement, prefix) => {
  let restored = String(text || "");
  const safeReplacement = replacement || "";
  const exactPattern = new RegExp(escapeRegExp(token), "g");
  const legacyPatterns = [
    new RegExp(`AAMSCHOOL${prefix}${index}TOKEN`, "gi"),
    new RegExp(`AAMSCHOOL${prefix}${index}टोकन`, "gi"),
  ];

  if (prefix === "KEEP") {
    legacyPatterns.push(new RegExp(`आमस्कूलकीप${index}टोकन`, "g"));
  }

  if (prefix === "TERM") {
    legacyPatterns.push(new RegExp(`आमस्कूलटर्म${index}टोकन`, "g"));
  }

  restored = restored.replace(exactPattern, safeReplacement);
  legacyPatterns.forEach((pattern) => {
    restored = restored.replace(pattern, safeReplacement);
  });

  return restored;
};

const normalizeQuestionLanguage = (lang) => {
  const value = String(lang || "").trim().toLowerCase();
  if (value === "hi" || value === "hindi") return "hi";
  return "en";
};

const getQuestionSourceLanguage = (question) =>
  normalizeQuestionLanguage(
    question?.language ??
      question?.laungauge ??
      question?.launguage ??
      question?.laungause
  );

const shouldSkipTranslation = (text, sourceLang) => {
  const value = String(text || "").trim();
  if (!value) return true;
  if (normalizeLanguage(sourceLang) === "hi") {
    return false;
  }
  if (fullyQuotedTextPattern.test(value)) return true;
  if (!/[a-z]/.test(value)) return true;
  return false;
};

const maskProtectedTerms = (text) => {
  let maskedText = String(text || "");
  const replacements = [];

  protectedHindiTerms.forEach((replacement, source) => {
    const pattern = new RegExp(`\\b${escapeRegExp(source)}\\b`, "gi");
    maskedText = maskedText.replace(pattern, (matched) => {
      const token = makePlaceholderToken("TERM", replacements.length);
      replacements.push({ token, replacement, source: matched, index: replacements.length });
      return token;
    });
  });

  return { maskedText, replacements };
};

const maskNonTranslatableSegments = (text, sourceLang = "en") => {
  if (normalizeLanguage(sourceLang) === "hi") {
    return { maskedText: String(text || ""), replacements: [] };
  }

  let maskedText = String(text || "");
  const replacements = [];
  const addReplacement = (segment) => {
    const token = makePlaceholderToken("KEEP", replacements.length);
    replacements.push({ token, replacement: segment, index: replacements.length });
    return token;
  };

  maskedText = maskedText.replace(/"[^"\r\n]*"|'[^'\r\n]*'/g, (matched) =>
    addReplacement(matched)
  );
  maskedText = maskedText.replace(/\b[A-Z][A-Z0-9./:+-]*\b|\b\d+(?:[./:+-]\d+)*\b/g, (matched) => {
    return addReplacement(matched);
  });

  return { maskedText, replacements };
};

const forceRestorePlaceholderPrefixes = (text, replacements, prefix) => {
  let restored = String(text || "");
  (replacements || []).forEach(({ replacement, source, index }) => {
    const safeReplacement = replacement || source || "";
    const basePattern = new RegExp(`${prefix}${index}`, "gi");
    const wrappedPattern = new RegExp(
      `${String.fromCharCode(0xe000)}\\s*${prefix}${index}\\s*${String.fromCharCode(0xe001)}`,
      "g"
    );
    const leadingPattern = new RegExp(
      `${String.fromCharCode(0xe000)}\\s*${prefix}${index}`,
      "g"
    );
    const trailingPattern = new RegExp(
      `${prefix}${index}\\s*${String.fromCharCode(0xe001)}`,
      "g"
    );

    restored = restored
      .replace(wrappedPattern, safeReplacement)
      .replace(leadingPattern, safeReplacement)
      .replace(trailingPattern, safeReplacement)
      .replace(basePattern, safeReplacement);
  });
  return restored;
};

const unmaskProtectedTerms = (text, replacements) => {
  let restored = String(text || "");
  (replacements || []).forEach(({ token, replacement, source, index }) => {
    restored = replacePlaceholderVariants(
      restored,
      { token, index },
      replacement || source || "",
      "TERM"
    );
  });
  return forceRestorePlaceholderPrefixes(restored, replacements, "TERM");
};

const unmaskNonTranslatableSegments = (text, replacements) => {
  let restored = String(text || "");
  (replacements || []).forEach(({ token, replacement, index }) => {
    restored = replacePlaceholderVariants(restored, { token, index }, replacement || "", "KEEP");
  });
  return forceRestorePlaceholderPrefixes(restored, replacements, "KEEP");
};

const hasStrongHindiSignal = (text) => {
  const value = String(text || "");
  const devanagariCount = (value.match(/[\u0900-\u097F]/g) || []).length;
  const letterCount = (value.match(/[A-Za-z\u0900-\u097F]/g) || []).length;
  if (!letterCount) return false;
  return devanagariCount >= Math.max(2, Math.floor(letterCount * 0.2));
};

const isSuspiciousTranslation = (source, translated) => {
  const src = String(source || "").trim().toLowerCase();
  const tgt = String(translated || "").trim().toLowerCase();
  if (!src || !tgt) return true;

  // Catch obvious bad translations for MCQ options.
  const suspiciousPhrases = [
    "i am fine",
    "how are you",
    "thank you",
    "hello",
  ];
  if (suspiciousPhrases.some((p) => tgt.includes(p))) return true;
  if ((tgt.includes("मैं") || tgt.includes("main ")) && src.split(/\s+/).length <= 4) {
    return true;
  }

  // If a single-word/short option turns into a long sentence, fallback.
  const srcWords = src.split(/\s+/).length;
  const tgtWords = tgt.split(/\s+/).length;
  if (srcWords <= 3 && tgtWords >= 6) return true;
  if (srcWords >= 8 && !hasStrongHindiSignal(tgt)) return true;
  if (/aamschoolterm\d+token/i.test(tgt)) return true;

  return false;
};

const hasHindiScript = (text) => /[\u0900-\u097F]/.test(String(text || ""));

const isOptionTranslationInvalid = (source, translated) => {
  const src = String(source || "").trim();
  const tgt = String(translated || "").trim();
  if (!src || !tgt) return true;
  if (isSuspiciousTranslation(src, tgt)) return true;

  // Block noisy/garbled outputs like random dots or odd tokens.
  if (/[.]{2,}|kgm|colombia/i.test(tgt)) return true;

  // For Hindi mode, translated option should normally contain Devanagari.
  // If it does not, keep source text instead of showing garbage.
  if (!hasHindiScript(tgt) && tgt.toLowerCase() !== src.toLowerCase()) return true;

  return false;
};

const isQuestionTranslationInvalid = (source, translated) => {
  const src = String(source || "").trim();
  const tgt = String(translated || "").trim();
  if (!src || !tgt) return true;
  if (isSuspiciousTranslation(src, tgt)) return true;

  return false;
};

const translateText = async (text, targetLang, sourceLang = "en") => {
  const value = String(text || "").trim();
  const lang = normalizeLanguage(targetLang);
  const source = normalizeLanguage(sourceLang);

  if (lang === source || shouldSkipTranslation(value, source)) return value;

  const protectedTerm = lang === "hi" && source === "en" ? getProtectedHindiTerm(value) : null;
  if (protectedTerm) return protectedTerm;

  const cacheKey = `${source}->${lang}::${value}`;
  if (translationCache.has(cacheKey)) return translationCache.get(cacheKey);
  const { maskedText: protectedMaskedText, replacements: protectedReplacements } =
    lang === "hi" && source === "en"
      ? maskProtectedTerms(value)
      : { maskedText: value, replacements: [] };
  const { maskedText, replacements: preservedReplacements } =
    maskNonTranslatableSegments(protectedMaskedText, source);

  const fetchMyMemoryTranslation = async (input, target) => {
    const res = await fetch(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(input)}&langpair=${source}|${target}`
    );
    if (!res.ok) throw new Error("MyMemory translation failed");
    const data = await res.json();
    return data?.responseData?.translatedText || input;
  };

  const fetchGoogleGtxTranslation = async (input, target) => {
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${source}&tl=${target}&dt=t&q=${encodeURIComponent(input)}`
    );
    if (!res.ok) throw new Error("Google translation failed");
    const data = await res.json();
    if (!Array.isArray(data) || !Array.isArray(data[0])) return input;
    const translated = data[0]
      .map((part) => (Array.isArray(part) ? part[0] : ""))
      .join("");
    return translated || input;
  };

  try {
    let translated = await fetchGoogleGtxTranslation(maskedText, lang);
    if (lang === "hi") {
      translated = applyHindiGlossaryFixes(
        unmaskNonTranslatableSegments(
          unmaskProtectedTerms(translated, protectedReplacements),
          preservedReplacements
        )
      );
      if (isSuspiciousTranslation(value, translated)) {
        try {
          const myMemoryTranslated = await fetchMyMemoryTranslation(maskedText, lang);
          translated = applyHindiGlossaryFixes(
            unmaskNonTranslatableSegments(
              unmaskProtectedTerms(myMemoryTranslated, protectedReplacements),
              preservedReplacements
            )
          );
        } catch {
          translated = value;
        }
      }
    }
    if (lang === "hi" && isSuspiciousTranslation(value, translated)) {
      return value;
    }
    translationCache.set(cacheKey, translated);
    return translated;
  } catch {
    if (lang === "hi") {
      try {
        const fallback = await fetchMyMemoryTranslation(maskedText, lang);
        const safeFallback = applyHindiGlossaryFixes(
          unmaskNonTranslatableSegments(
            unmaskProtectedTerms(fallback, protectedReplacements),
            preservedReplacements
          )
        );
        if (isSuspiciousTranslation(value, safeFallback)) {
          translationCache.set(cacheKey, value);
          return value;
        }
        translationCache.set(cacheKey, safeFallback);
        return safeFallback;
      } catch {
        translationCache.set(cacheKey, value);
        return value;
      }
    }
    return value;
  }
};

const translateOptionToHindi = async (optionText, targetLang = "hi", sourceLang = "en") => {
  const protectedTerm =
    normalizeLanguage(targetLang) === "hi" && normalizeLanguage(sourceLang) === "en"
      ? getProtectedHindiTerm(optionText)
      : null;
  if (protectedTerm) return protectedTerm;

  const translated = await translateText(optionText, targetLang, sourceLang);
  if (normalizeLanguage(targetLang) !== "hi") {
    return translated;
  }
  if (isOptionTranslationInvalid(optionText, translated)) {
    return optionText;
  }
  return translated;
};

const translateQuestionToHindi = async (questionText, targetLang = "hi", sourceLang = "en") => {
  const translated = await translateText(questionText, targetLang, sourceLang);
  if (normalizeLanguage(targetLang) !== "hi") {
    return translated;
  }
  if (isQuestionTranslationInvalid(questionText, translated)) {
    return questionText;
  }
  return translated;
};

const mapWithConcurrency = async (items, limit, mapper) => {
  const results = new Array(items.length);
  let index = 0;

  const worker = async () => {
    while (index < items.length) {
      const currentIndex = index++;
      results[currentIndex] = await mapper(items[currentIndex], currentIndex);
    }
  };

  const workers = Array.from(
    { length: Math.max(1, Math.min(limit, items.length)) },
    () => worker()
  );
  await Promise.all(workers);
  return results;
};

const formatStatsDateTime = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const clampPercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(100, numeric));
};

const formatPercent = (value) => `${Number(value || 0).toFixed(0)}%`;

const normalizeLeaderboardEntry = (entry, index = 0) => {
  if (!entry || typeof entry !== "object") return null;

  const userId = entry?.user?.userId ?? entry?.user?.id ?? entry?.userId ?? entry?.id ?? null;
  const username =
    entry?.user?.username ??
    entry?.user?.name ??
    entry?.username ??
    entry?.name ??
    "User";

  return {
    ...entry,
    rank: Math.max(1, Number(entry?.rank) || index + 1),
    score: Math.max(0, Number(entry?.score) || 0),
    totalMarks: Math.max(
      0,
      Number(entry?.totalMarks ?? entry?.totalQuestions ?? entry?.questionCount) || 0
    ),
    accuracy: clampPercent(entry?.accuracy),
    correctAnswers: Math.max(0, Number(entry?.correctAnswers) || 0),
    wrongAnswers: Math.max(0, Number(entry?.wrongAnswers) || 0),
    attemptedQuestions: Math.max(
      0,
      Number(entry?.attemptedQuestions ?? entry?.attemptCount) || 0
    ),
    timeTakenSeconds: Math.max(0, Number(entry?.timeTakenSeconds ?? entry?.timeTaken) || 0),
    submittedAt: entry?.submittedAt || entry?.createdAt || entry?.updatedAt || null,
    isCurrentUser: Boolean(entry?.isCurrentUser),
    user: {
      ...(entry?.user && typeof entry.user === "object" ? entry.user : {}),
      userId,
      id: userId,
      username,
      name: username,
    },
  };
};

const normalizeLeaderboardPayload = (payload) => {
  const rawEntries = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.entries)
    ? payload.entries
    : Array.isArray(payload?.leaderboard)
    ? payload.leaderboard
    : Array.isArray(payload?.results)
    ? payload.results
    : [];

  const entries = rawEntries
    .map((entry, index) => normalizeLeaderboardEntry(entry, index))
    .filter(Boolean);

  return {
    ...(payload && typeof payload === "object" ? payload : {}),
    entries,
    quizTitle:
      payload?.quizTitle || payload?.title || payload?.quizName || "Daily Current Affairs Leaderboard",
    totalParticipants: Math.max(
      entries.length,
      Number(payload?.totalParticipants ?? payload?.participants ?? payload?.count) || 0
    ),
    generatedAt:
      payload?.generatedAt || payload?.updatedAt || payload?.lastUpdatedAt || payload?.createdAt || null,
  };
};

const normalizeCurrentAffairsStats = (stats, fallback = {}) => {
  if (!stats || typeof stats !== "object") return null;

  const totalQuestions = Math.max(
    0,
    Number(
      stats?.totalQuestions ??
        stats?.totalMarks ??
        stats?.questionCount ??
        fallback?.totalQuestions ??
        fallback?.totalMarks
    ) || 0
  );
  const attemptedQuestions = Math.max(
    0,
    Number(stats?.attemptedQuestions ?? stats?.attemptCount ?? fallback?.attemptedQuestions) || 0
  );
  const correctAnswers = Math.max(
    0,
    Number(stats?.correctAnswers ?? fallback?.correctAnswers ?? stats?.score) || 0
  );
  const wrongAnswers = Math.max(
    0,
    Number(
      stats?.wrongAnswers ??
        fallback?.wrongAnswers ??
        Math.max(attemptedQuestions - correctAnswers, 0)
    ) || 0
  );
  const score = Math.max(0, Number(stats?.score ?? fallback?.score ?? correctAnswers) || 0);
  const accuracy = clampPercent(
    stats?.accuracy ??
      fallback?.accuracy ??
      (attemptedQuestions ? (correctAnswers / attemptedQuestions) * 100 : 0)
  );
  const timeTakenSeconds = Math.max(
    0,
    Number(stats?.timeTakenSeconds ?? stats?.timeTaken ?? fallback?.timeTakenSeconds) || 0
  );

  return {
    ...fallback,
    ...stats,
    attemptId: stats?.attemptId ?? fallback?.attemptId ?? null,
    userId: stats?.userId ?? fallback?.userId ?? stats?.user?.userId ?? stats?.user?.id ?? null,
    totalQuestions,
    totalMarks: totalQuestions,
    attemptedQuestions,
    correctAnswers,
    wrongAnswers,
    score,
    accuracy,
    timeTakenSeconds,
    submittedAt:
      stats?.submittedAt || stats?.createdAt || stats?.updatedAt || fallback?.submittedAt || null,
  };
};

const normalizeOptionValue = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const areOptionsEqual = (left, right) =>
  normalizeOptionValue(left) && normalizeOptionValue(left) === normalizeOptionValue(right);
const normalizeDifficultyLabel = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized.includes("easy")) return "easy";
  if (normalized.includes("hard")) return "hard";
  return "medium";
};

const isCorrectAnswerForQuestion = (question, answer) => {
  if (!question || !answer) return false;
  const options = Array.isArray(question?.options) ? question.options : [];
  const correctOption = question?.correct_option || "";
  const selectedCanonicalOption = answer?.canonicalOption || "";

  if (areOptionsEqual(selectedCanonicalOption, correctOption)) {
    return true;
  }

  if (typeof answer?.optionIndex === "number") {
    const optionByIndex = options[answer.optionIndex];
    if (areOptionsEqual(optionByIndex, correctOption)) {
      return true;
    }
  }

  const resolvedCorrectIndex = options.findIndex((option) => areOptionsEqual(option, correctOption));
  if (resolvedCorrectIndex >= 0 && resolvedCorrectIndex === answer?.optionIndex) {
    return true;
  }

  return false;
};

const buildCurrentAffairsSubmitPayload = ({
  questions = [],
  userAnswers = {},
  totalQuizSeconds = 0,
  remainingSeconds = 0,
  startedAt = null,
  autoSubmit = false,
}) => {
  const elapsedSeconds = totalQuizSeconds
    ? Math.max(1, totalQuizSeconds - Math.max(remainingSeconds ?? totalQuizSeconds, 0))
    : Math.max(1, Math.floor((Date.now() - Number(startedAt || Date.now())) / 1000));

  return {
    timeTakenSeconds: autoSubmit ? Math.max(totalQuizSeconds, 1) : elapsedSeconds,
    answers: questions.flatMap((question, questionIndex) => {
      const answer = userAnswers[questionIndex];
      if (!answer) return [];

      const optionByIndex =
        typeof answer?.optionIndex === "number"
          ? question?.options?.[answer.optionIndex]
          : "";
      const selectedOption = String(
        answer?.canonicalOption || optionByIndex || answer?.selectedOption || ""
      ).trim();

      if (!selectedOption) return [];

      return [{ questionIndex, selectedOption }];
    }),
  };
};

const fetchCurrentAffairsLeaderboard = async (slugWithId) => {
  const parsedId = parseIdFromSlugWithId(slugWithId);
  const endpoints = [
    `/api/current-affairs-quizzes/${encodeURIComponent(slugWithId)}/leaderboard`,
  ];

  if (parsedId) {
    endpoints.push(`/api/current-affairs-quizzes/${parsedId}/leaderboard`);
    endpoints.push(`/quizzes/${parsedId}/leaderboard`);
  }

  let lastError = null;
  for (const endpoint of endpoints) {
    try {
      const response = await apiFetch(endpoint, { auth: true });
      const data = await readJsonSafe(response);
      if (response.ok) return normalizeLeaderboardPayload(data);
      lastError = new Error(data?.message || "Unable to load leaderboard. Please try again.");
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Unable to load leaderboard. Please try again.");
};

function CurrentAffairsStatsSummary({ stats, isPreviousAttempt = false }) {
  if (!stats) return null;

  const totalQuestions = Number(stats?.totalQuestions || 0);
  const attemptedQuestions = Number(stats?.attemptedQuestions || 0);
  const correctAnswers = Number(stats?.correctAnswers || 0);
  const wrongAnswers = Number(stats?.wrongAnswers || 0);
  const score = Number(stats?.score || 0);
  const accuracy = Number(stats?.accuracy || 0);
  const unattemptedCount = Math.max(0, totalQuestions - attemptedQuestions);
  const scorePercent = totalQuestions
    ? Number(((score / totalQuestions) * 100).toFixed(2))
    : 0;
  const isCelebration = scorePercent >= 60;

  return (
    <div className={`quiz-result-card ${isCelebration ? "celebrate" : ""}`}>
      {isCelebration ? (
        <div className="quiz-balloon-burst" aria-hidden="true">
          <span className="quiz-balloon balloon-1" />
          <span className="quiz-balloon balloon-2" />
          <span className="quiz-balloon balloon-3" />
          <span className="quiz-balloon balloon-4" />
          <span className="quiz-balloon balloon-5" />
          <span className="quiz-balloon balloon-6" />
        </div>
      ) : null}
      <h2>{isPreviousAttempt ? "Your Previous Attempt" : `Quiz Completed ${isCelebration ? "🎉" : "✓"}`}</h2>
      <p className="quiz-result-status">
        {isPreviousAttempt
          ? "Your saved result has been loaded from the server."
          : isCelebration
          ? "Excellent performance. Keep it up."
          : "Your attempt has been saved successfully."}
      </p>

      <div className="quiz-score-hero">
        <div className="quiz-score-ring" style={{ "--score": `${scorePercent}%` }}>
          <strong>{scorePercent}%</strong>
          <span>Score</span>
        </div>
        <div className="quiz-score-text">
          <h3>{stats?.quizTitle || "Daily Current Affairs Quiz"}</h3>
          <p>
            You answered <strong>{correctAnswers}</strong> out of{" "}
            <strong>{totalQuestions}</strong> correctly.
          </p>
        </div>
      </div>

      <div className="quiz-performance-bars" aria-label="Result breakdown">
        <div className="quiz-bar-row">
          <div className="quiz-bar-label">Correct</div>
          <div className="quiz-bar-track">
            <span
              className="quiz-bar-fill correct"
              style={{ width: `${totalQuestions ? (correctAnswers / totalQuestions) * 100 : 0}%` }}
            />
          </div>
          <div className="quiz-bar-value">{correctAnswers}</div>
        </div>
        <div className="quiz-bar-row">
          <div className="quiz-bar-label">Wrong</div>
          <div className="quiz-bar-track">
            <span
              className="quiz-bar-fill wrong"
              style={{ width: `${totalQuestions ? (wrongAnswers / totalQuestions) * 100 : 0}%` }}
            />
          </div>
          <div className="quiz-bar-value">{wrongAnswers}</div>
        </div>
        <div className="quiz-bar-row">
          <div className="quiz-bar-label">Unattempted</div>
          <div className="quiz-bar-track">
            <span
              className="quiz-bar-fill skipped"
              style={{ width: `${totalQuestions ? (unattemptedCount / totalQuestions) * 100 : 0}%` }}
            />
          </div>
          <div className="quiz-bar-value">{unattemptedCount}</div>
        </div>
      </div>

      <div className="quiz-summary-cards" aria-label="Result summary">
        <div className="quiz-summary-card neutral">
          <span>Attempted</span>
          <strong>{attemptedQuestions}</strong>
        </div>
        <div className="quiz-summary-card success">
          <span>Correct</span>
          <strong>{correctAnswers}</strong>
        </div>
        <div className="quiz-summary-card danger">
          <span>Wrong</span>
          <strong>{wrongAnswers}</strong>
        </div>
        <div className="quiz-summary-card info">
          <span>Score</span>
          <strong>
            {score} / {totalQuestions}
          </strong>
        </div>
        <div className={`quiz-summary-card ${accuracy >= 60 ? "success" : "warning"}`}>
          <span>Accuracy</span>
          <strong>{accuracy.toFixed(2)}%</strong>
        </div>
        <div className="quiz-summary-card neutral">
          <span>Submitted</span>
          <strong>{formatStatsDateTime(stats?.submittedAt)}</strong>
        </div>
        <div className="quiz-summary-card neutral">
          <span>Time Taken</span>
          <strong>{Number(stats?.timeTakenSeconds || 0)}s</strong>
        </div>
      </div>
    </div>
  );
}

export { CurrentAffairsStatsSummary };

export function CurrentAffairsQuizListPage() {
  const navigate = useNavigate();
  const currentUserId = getUser()?.id ?? null;
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const canonicalUrl = buildCanonicalUrl("/daily-current-affairs-quiz");
  const pageTitle = "Daily Current Affairs Quiz - AamSchool";
  const pageDescription =
    "Attempt daily current affairs quiz with first-attempt answer capture and post-submit solutions.";

  useEffect(() => {
    const controller = new AbortController();

    const loadQuizzes = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/current-affairs-quizzes`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Failed to load current affairs quizzes.");
        const data = await res.json();
        setQuizzes(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message || "Something went wrong.");
      } finally {
        setLoading(false);
      }
    };

    loadQuizzes();
    return () => controller.abort();
  }, []);

  const handleProtectedNavigation = useCallback(
    (event, targetPath) => {
      if (isAuthenticated()) return;
      event.preventDefault();
      redirectToLoginForCurrentAffairsQuiz(navigate, targetPath);
    },
    [navigate]
  );

  return (
    <div className="quiz-page">
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>

      <h1>Daily Current Affairs Quiz</h1>

      {loading && <p>Loading quizzes...</p>}
      {error && <p className="quiz-error">{error}</p>}

      {!loading && !error && (
        <div className="quiz-list-card">
          {quizzes.length === 0 && <p>No quiz available right now.</p>}

          {quizzes.map((quiz) => {
            const slugWithId = quiz?.slugWithId || makeSlugWithId(quiz.title, quiz.id);
            const instructionsPath = `/daily-current-affairs-quiz/${slugWithId}/instructions`;
            const startPath = `/daily-current-affairs-quiz/${slugWithId}/start?lang=hi`;
            const leaderboardPath = `/daily-current-affairs-quiz/${slugWithId}/leaderboard`;
            const attemptKey = makeCurrentAffairsAttemptKey(slugWithId);
            const lastAttempt = getQuizAttemptSummary(attemptKey, currentUserId);
            const attempted = Boolean(lastAttempt) || isQuizAttempted(attemptKey, currentUserId);
            const totalQuestions = Number(lastAttempt?.totalMarks || 0);
            const score = Number(lastAttempt?.score || 0);
            const lastAttemptScore = totalQuestions
              ? Math.max(0, Math.min(100, (score / totalQuestions) * 100))
              : 0;
            const isPassingScore = lastAttemptScore >= 60;
            const attemptAccent = isPassingScore
              ? "rgba(46, 175, 83, 0.18)"
              : "rgba(245, 158, 11, 0.18)";
            const attemptGlow = isPassingScore
              ? "rgba(46, 175, 83, 0.24)"
              : "rgba(245, 158, 11, 0.22)";
            return (
              <div
                className={`quiz-list-row ${attempted ? "attempted-score-row" : ""}`}
                key={quiz.id}
                style={
                  attempted
                    ? {
                        "--attempt-fill": `${lastAttemptScore}%`,
                        "--attempt-accent": attemptAccent,
                        "--attempt-glow": attemptGlow,
                      }
                    : undefined
                }
              >
                <div className="quiz-title-wrap">
                  <Link
                    className={`quiz-title-link ${attempted ? "attempted-link" : ""}`}
                    to={instructionsPath}
                    onClick={(event) => handleProtectedNavigation(event, instructionsPath)}
                  >
                    {quiz.title}
                  </Link>
                  {attempted && lastAttempt ? (
                    <div className="quiz-last-attempt-card" aria-label="Last attempt score">
                      <div className="quiz-last-attempt-copy">
                        <span className="quiz-last-attempt-label">Your last attempt</span>
                        <div className="quiz-last-attempt-stats">
                          <strong>{`${Math.round(lastAttemptScore)}%`}</strong>
                          <span>{`${score} / ${totalQuestions}`}</span>
                        </div>
                        <span>{`${lastAttempt?.correctAnswers || 0} correct in ${
                          lastAttempt?.timeTakenSeconds || 0
                        }s`}</span>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="quiz-leaderboard-actions">
                  {attempted ? (
                    <Link
                      className="quiz-secondary-btn"
                      to={leaderboardPath}
                      onClick={(event) => handleProtectedNavigation(event, leaderboardPath)}
                    >
                      View Score
                    </Link>
                  ) : null}
                  <Link
                    className="quiz-start-btn"
                    to={startPath}
                    onClick={(event) => handleProtectedNavigation(event, startPath)}
                  >
                    {attempted ? "Reattempt Quiz" : "Start Quiz"}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CurrentAffairsQuizInstructionsPage() {
  const { slugWithId } = useParams();
  const navigate = useNavigate();
  const [quizTitle, setQuizTitle] = useState("Current Affairs Quiz");
  const [language, setLanguage] = useState("hi");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const canonicalUrl = buildCanonicalUrl(
    `/daily-current-affairs-quiz/${encodeURIComponent(slugWithId || "")}/instructions`
  );
  const pageTitle = `${quizTitle} - Quiz Instructions | AamSchool`;
  const pageDescription = `Read instructions and choose language before starting ${quizTitle}.`;

  useEffect(() => {
    if (isAuthenticated()) return;
    redirectToLoginForCurrentAffairsQuiz(
      navigate,
      `${window.location.pathname}${window.location.search}`
    );
  }, [navigate]);

  useEffect(() => {
    const controller = new AbortController();

    const loadQuiz = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/current-affairs-quizzes/${slugWithId}`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error("Failed to load quiz details.");
        const data = await res.json();
        setQuizTitle(data?.title || "Current Affairs Quiz");
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message || "Something went wrong.");
      } finally {
        setLoading(false);
      }
    };

    loadQuiz();
    return () => controller.abort();
  }, [slugWithId]);

  return (
    <div className="quiz-page">
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>

      <h1>{quizTitle}</h1>

      {loading && <p>Loading instructions...</p>}
      {error && <p className="quiz-error">{error}</p>}

      {!loading && !error && (
        <div className="quiz-instructions">
          <h3>General Instructions</h3>
          <ul>
            <li>Read each question carefully before selecting your answer.</li>
            <li>First attempt captures your responses only; solution is available after submit.</li>
            <li>On reattempt, correct/wrong options and description are shown instantly.</li>
            <li>Click Next Question to continue.</li>
          </ul>

          <div className="lang-selector">
            <label htmlFor="quiz-lang">Choose Language:</label>
            <select
              id="quiz-lang"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="hi">Hindi (Default)</option>
              <option value="en">English</option>
            </select>
          </div>

          <div className="quiz-actions">
            <button
              className="quiz-start-btn"
              onClick={() =>
                navigate(`/daily-current-affairs-quiz/${slugWithId}/start?lang=${language}`)
              }
            >
              Begin Quiz
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function CurrentAffairsQuizPlayPage() {
  const { slugWithId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const language = searchParams.get("lang") || "hi";

  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [quizLanguage, setQuizLanguage] = useState(normalizeLanguage(language));
  const [hindiQuiz, setHindiQuiz] = useState(null);
  const [isHindiReady, setIsHindiReady] = useState(false);
  const [translationStatus, setTranslationStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [savedStats, setSavedStats] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
  const [isWrapConfirmOpen, setIsWrapConfirmOpen] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);
  const canonicalUrl = buildCanonicalUrl(
    `/daily-current-affairs-quiz/${encodeURIComponent(slugWithId || "")}/start`
  );
  const currentUserId = getUser()?.id;

  useEffect(() => {
    if (isAuthenticated()) return;
    redirectToLoginForCurrentAffairsQuiz(
      navigate,
      `${window.location.pathname}${window.location.search}`
    );
  }, [navigate]);

  useEffect(() => {
    const controller = new AbortController();

    const loadQuiz = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/current-affairs-quizzes/${slugWithId}`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error("Failed to load quiz.");
        const data = await res.json();
        setQuiz(data);
        setStartedAt(Date.now());
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message || "Something went wrong.");
      } finally {
        setLoading(false);
      }
    };

    loadQuiz();
    return () => controller.abort();
  }, [slugWithId]);

  useEffect(() => {
    setQuizLanguage(normalizeLanguage(language));
  }, [language]);

  const questions = useMemo(() => quiz?.questions || [], [quiz]);
  const hasQuestionsNeedingTranslation = useMemo(
    () => questions.some((question) => getQuestionSourceLanguage(question) !== quizLanguage),
    [questions, quizLanguage]
  );
  const displayedQuiz =
    hasQuestionsNeedingTranslation && isHindiReady && hindiQuiz ? hindiQuiz : quiz;
  const displayedQuestions = displayedQuiz?.questions || questions;
  const pageTitle = `${displayedQuiz?.title || quiz?.title || "Current Affairs Quiz"} | AamSchool`;
  const pageDescription = `Daily current affairs quiz question ${currentIndex + 1} of ${
    questions.length || 0
  } with first-attempt answer capture and reattempt review mode.`;
  const attemptCacheKey = useMemo(
    () => makeCurrentAffairsAttemptKey(slugWithId),
    [slugWithId]
  );
  const isReviewMode = isQuizAttempted(attemptCacheKey, currentUserId);

  const currentQuestion = questions[currentIndex];
  const currentDisplayedQuestion = displayedQuestions[currentIndex];
  const selectedAnswer = userAnswers[currentIndex] || null;
  const selectedOption = selectedAnswer?.selectedOption || "";
  const isLastQuestion = currentIndex === questions.length - 1;
  const shouldBlockForHindiTranslation = hasQuestionsNeedingTranslation && !isHindiReady;
  const totalQuizSeconds = questions.length * 60;

  const attemptedCount = Object.keys(userAnswers).filter((key) => userAnswers[key]).length;
  const correctCount = savedStats
    ? Number(savedStats?.correctAnswers || 0)
    : questions.reduce((count, question, index) => {
        if (isCorrectAnswerForQuestion(question, userAnswers[index])) {
          return count + 1;
        }
        return count;
      }, 0);
  const wrongCount = savedStats
    ? Number(savedStats?.wrongAnswers || 0)
    : Math.max(0, attemptedCount - correctCount);
  const totalQuestions = savedStats
    ? Number(savedStats?.totalQuestions || questions.length)
    : questions.length;
  const unattemptedCount = Math.max(
    0,
    (savedStats ? Number(savedStats?.attemptedQuestions || attemptedCount) : totalQuestions) >= 0
      ? totalQuestions - (savedStats ? Number(savedStats?.attemptedQuestions || 0) : attemptedCount)
      : 0
  );
  const accuracy = savedStats
    ? Number(savedStats?.accuracy || 0).toFixed(2)
    : attemptedCount
    ? ((correctCount / attemptedCount) * 100).toFixed(2)
    : "0.00";
  const scorePercent = totalQuestions
    ? Number((((savedStats ? Number(savedStats?.score || 0) : correctCount) / totalQuestions) * 100).toFixed(2))
    : 0;
  const isCelebration = scorePercent >= 60;

  useEffect(() => {
    if (!totalQuizSeconds) {
      setRemainingSeconds(null);
      return;
    }
    setRemainingSeconds(totalQuizSeconds);
    setHasAutoSubmitted(false);
  }, [totalQuizSeconds]);

  useEffect(() => {
    if (!slugWithId || !isAuthenticated()) {
      setSavedStats(null);
      return;
    }

    let mounted = true;

    const loadSavedStats = async () => {
      try {
        const response = await apiFetch(`/api/current-affairs-quizzes/${slugWithId}/my-stats`, {
          auth: true,
        });
        const data = await readJsonSafe(response);

        if (!mounted) return;

        if (response.status === 403) {
          setSavedStats(null);
          return;
        }

        if (response.status === 404) {
          setSavedStats(null);
          return;
        }

        if (!response.ok) {
          throw new Error(data?.message || "Unable to load your previous attempt.");
        }

        if (data?.attemptId) {
          setSavedStats(data);
          markQuizAttempted(attemptCacheKey, {
            userId: data?.userId,
            score: data?.score,
            totalMarks: data?.totalQuestions,
            accuracy: data?.accuracy,
            correctAnswers: data?.correctAnswers,
            wrongAnswers: data?.wrongAnswers,
            attemptedQuestions: data?.attemptedQuestions,
            timeTakenSeconds: data?.timeTakenSeconds,
            submittedAt: data?.submittedAt,
          });
        } else {
          setSavedStats(null);
        }
      } catch (err) {
        if (!mounted) return;
      }
    };

    loadSavedStats();

    return () => {
      mounted = false;
    };
  }, [attemptCacheKey, slugWithId]);

  useEffect(() => {
    let mounted = true;

    const buildHindiQuestions = async () => {
      if (!questions.length) return;

      if (!hasQuestionsNeedingTranslation) {
        if (!mounted) return;
        setHindiQuiz(null);
        setIsHindiReady(true);
        setTranslationStatus("");
        return;
      }

      setIsHindiReady(false);
      setTranslationStatus("Preparing translation...");

      const cacheKey = `${HINDI_QUIZ_CACHE_PREFIX}${slugWithId}::${quizLanguage}`;
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (
            parsed &&
            Array.isArray(parsed.questions) &&
            parsed.questions.length === questions.length
          ) {
            if (!mounted) return;
            setHindiQuiz(parsed);
            setIsHindiReady(true);
            setTranslationStatus("");
            return;
          }
        }
      } catch {
        // Ignore cache read/parse issues and rebuild translation.
      }

      try {
        const translatedTitle =
          quizLanguage === "hi" ? await translateText(quiz?.title, "hi", "en") : quiz?.title;
        const translatedTopic =
          quizLanguage === "hi" ? await translateText(quiz?.topic, "hi", "en") : quiz?.topic;
        const translated = await mapWithConcurrency(
          questions,
          3,
          async (q, idx) => {
            const sourceLanguage = getQuestionSourceLanguage(q);
            const [translatedQuestion, translatedDescription, translatedExamName, translatedOptions] =
              await Promise.all([
                translateQuestionToHindi(q.question, quizLanguage, sourceLanguage),
                translateText(q.details_description, quizLanguage, sourceLanguage),
                translateText(q.exam_name, quizLanguage, sourceLanguage),
                Promise.all(
                  (q.options || []).map((option) =>
                    translateOptionToHindi(option, quizLanguage, sourceLanguage)
                  )
                ),
              ]);

            if (mounted && idx % 5 === 0) {
              setTranslationStatus(
                `Translating questions... ${Math.min(idx + 1, questions.length)}/${questions.length}`
              );
            }

            return {
              ...q,
              question: translatedQuestion || q.question,
              details_description: translatedDescription || q.details_description,
              exam_name: translatedExamName || q.exam_name,
              options: translatedOptions.length ? translatedOptions : q.options,
            };
          }
        );
        if (!mounted) return;
        const translatedQuiz = {
          ...quiz,
          title: translatedTitle || quiz?.title,
          topic: translatedTopic || quiz?.topic,
          questions: translated,
        };
        setHindiQuiz(translatedQuiz);
        setIsHindiReady(true);
        setTranslationStatus("");
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(translatedQuiz));
        } catch {
          // Ignore cache write failures.
        }
      } catch {
        if (!mounted) return;
        setHindiQuiz(null);
        setIsHindiReady(false);
        setTranslationStatus("Translation unavailable right now. Showing original language.");
      } finally {
        if (mounted) {
          setTranslationStatus("");
        }
      }
    };

    buildHindiQuestions();

    return () => {
      mounted = false;
    };
  }, [hasQuestionsNeedingTranslation, questions, quiz, quizLanguage, slugWithId]);

  const handleSelectOption = (optionIndex) => {
    if (!currentQuestion) return;
    const displayedOption = currentDisplayedQuestion?.options?.[optionIndex];
    const englishOption = currentQuestion.options?.[optionIndex];
    if (!englishOption || !displayedOption) return;
    setUserAnswers((prev) => ({
      ...prev,
      [currentIndex]: {
        selectedOption: displayedOption,
        canonicalOption: englishOption,
        optionIndex,
      },
    }));
    setSubmitError("");
  };

  const handleNext = () => {
    if (isLastQuestion) {
      setIsWrapConfirmOpen(true);
      return;
    }
    setCurrentIndex((prev) => Math.min(prev + 1, questions.length - 1));
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const submitQuizAttempt = useCallback(async ({ autoSubmit = false } = {}) => {
    if (!isAuthenticated()) {
      setIsFinishModalOpen(false);
      redirectToLoginForCurrentAffairsQuiz(
        navigate,
        `${window.location.pathname}${window.location.search}`
      );
      return;
    }

    if (!slugWithId) {
      setSubmitError("Unable to submit this quiz right now.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setIsFinishModalOpen(false);

    try {
      const payload = buildCurrentAffairsSubmitPayload({
        questions,
        userAnswers,
        totalQuizSeconds,
        remainingSeconds,
        startedAt,
        autoSubmit,
      });

      const response = await apiFetch(`/api/current-affairs-quizzes/${encodeURIComponent(slugWithId)}/submit`, {
        method: "POST",
        auth: true,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await readJsonSafe(response);

      if (response.status === 403) {
        throw new Error(data?.message || "You are not allowed to submit this quiz.");
      }

      if (!response.ok) {
        throw new Error(data?.message || "Unable to submit your quiz.");
      }

      setSavedStats(data);
      setIsSubmitted(true);
      markQuizAttempted(attemptCacheKey, {
        userId: data?.userId,
        score: data?.score,
        totalMarks: data?.totalQuestions,
        accuracy: data?.accuracy,
        correctAnswers: data?.correctAnswers,
        wrongAnswers: data?.wrongAnswers,
        attemptedQuestions: data?.attemptedQuestions,
        timeTakenSeconds: data?.timeTakenSeconds,
        submittedAt: data?.submittedAt,
      });
      saveAttemptSolutions(attemptCacheKey, {
        savedAt: new Date().toISOString(),
        quizTitle: quiz?.title || "",
        answers: questions.map((question, index) => {
          const selected = userAnswers[index];
          const selectedText = selected?.canonicalOption || selected?.selectedOption || "";
          const correctText = question?.correct_option || "";
          return {
            question: question?.question || "",
            options: Array.isArray(question?.options) ? question.options : [],
            selectedOption: selectedText,
            correctOption: correctText,
            detailsDescription: question?.details_description || "",
            isCorrect: isCorrectAnswerForQuestion(question, selected),
          };
        }),
      });
      navigate(`/daily-current-affairs-quiz/${slugWithId}/leaderboard`, { replace: true });
    } catch (err) {
      setSubmitError(err.message || "Unable to submit your quiz.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    attemptCacheKey,
    navigate,
    questions,
    quiz?.title,
    remainingSeconds,
    slugWithId,
    startedAt,
    totalQuizSeconds,
    userAnswers,
  ]);

  const handleFinish = () => {
    if (isSubmitting) {
      return;
    }

    if (!slugWithId) {
      setSubmitError("Unable to submit this quiz right now.");
      return;
    }

    setSubmitError("");
    setIsFinishModalOpen(true);
  };

  const handleQuestionLanguageChange = (value) => {
    setQuizLanguage(normalizeLanguage(value));
  };

  const getQuestionStatusClass = (index) => {
    const selected = userAnswers[index];
    if (!selected) return "unattempted";
    if (!isReviewMode) return "selected";
    const question = questions[index];
    return isCorrectAnswerForQuestion(question, selected) ? "correct" : "wrong";
  };

  useEffect(() => {
    if (
      loading ||
      error ||
      isSubmitting ||
      shouldBlockForHindiTranslation ||
      !questions.length ||
      remainingSeconds == null
    ) {
      return undefined;
    }

    if (remainingSeconds <= 0) {
      if (hasAutoSubmitted) {
        return undefined;
      }
      setHasAutoSubmitted(true);
      submitQuizAttempt({ autoSubmit: true });
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setRemainingSeconds((prev) => Math.max((prev ?? 0) - 1, 0));
    }, 1000);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [
    error,
    hasAutoSubmitted,
    isSubmitting,
    loading,
    questions.length,
    remainingSeconds,
    shouldBlockForHindiTranslation,
    submitQuizAttempt,
  ]);

  const getOptionClass = (optionIndex) => {
    const englishOption = currentQuestion.options?.[optionIndex];
    if (!englishOption) return "quiz-option";
    if (!selectedAnswer) return "quiz-option";
    if (!isReviewMode) {
      return selectedAnswer?.optionIndex === optionIndex ? "quiz-option selected" : "quiz-option";
    }
    if (areOptionsEqual(englishOption, currentQuestion.correct_option)) return "quiz-option correct";
    if (
      selectedAnswer?.optionIndex === optionIndex &&
      !areOptionsEqual(englishOption, currentQuestion.correct_option)
    ) {
      return "quiz-option wrong";
    }
    return "quiz-option";
  };

  if (loading) {
    return (
      <div className="quiz-page">
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quiz-page">
        <p className="quiz-error">{error}</p>
      </div>
    );
  }

  if (!quiz || !Array.isArray(quiz.questions) || quiz.questions.length === 0) {
    return (
      <div className="quiz-page">
        <p className="quiz-error">No questions found for this quiz.</p>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="quiz-page">
        <h1>{quiz.title}</h1>
        <div className={`quiz-result-card ${isCelebration ? "celebrate" : ""}`}>
          {isCelebration ? (
            <div className="quiz-balloon-burst" aria-hidden="true">
              <span className="quiz-balloon balloon-1" />
              <span className="quiz-balloon balloon-2" />
              <span className="quiz-balloon balloon-3" />
              <span className="quiz-balloon balloon-4" />
              <span className="quiz-balloon balloon-5" />
              <span className="quiz-balloon balloon-6" />
            </div>
          ) : null}
          <h2>Quiz Completed {isCelebration ? "🎉" : "✅"}</h2>
          {isCelebration ? (
            <p className="quiz-result-status">Excellent performance. Keep it up. 🎈</p>
          ) : null}
          <div className="quiz-score-hero">
            <div
              className="quiz-score-ring"
              style={{ "--score": `${scorePercent}%` }}
            >
              <strong>{scorePercent}%</strong>
              <span>Score</span>
            </div>
            <div className="quiz-score-text">
              <h3>{isCelebration ? "Great Job! 🌟" : "Good Try! 💪"}</h3>
              <p>
                You answered <strong>{correctCount}</strong> out of{" "}
                <strong>{questions.length}</strong> correctly.
              </p>
            </div>
          </div>

          <div className="quiz-performance-bars" aria-label="Result breakdown">
            <div className="quiz-bar-row">
              <div className="quiz-bar-label">Correct</div>
              <div className="quiz-bar-track">
                <span
                  className="quiz-bar-fill correct"
                  style={{
                    width: `${questions.length ? (correctCount / questions.length) * 100 : 0}%`,
                  }}
                />
              </div>
              <div className="quiz-bar-value">{correctCount}</div>
            </div>
            <div className="quiz-bar-row">
              <div className="quiz-bar-label">Wrong</div>
              <div className="quiz-bar-track">
                <span
                  className="quiz-bar-fill wrong"
                  style={{
                    width: `${questions.length ? (wrongCount / questions.length) * 100 : 0}%`,
                  }}
                />
              </div>
              <div className="quiz-bar-value">{wrongCount}</div>
            </div>
            <div className="quiz-bar-row">
              <div className="quiz-bar-label">Unattempted</div>
              <div className="quiz-bar-track">
                <span
                  className="quiz-bar-fill skipped"
                  style={{
                    width: `${questions.length ? (unattemptedCount / questions.length) * 100 : 0}%`,
                  }}
                />
              </div>
              <div className="quiz-bar-value">{unattemptedCount}</div>
            </div>
          </div>

          <div className="quiz-summary-cards" aria-label="Result summary">
            <div className="quiz-summary-card neutral">
              <span>Total Questions</span>
              <strong>{questions.length}</strong>
            </div>
            <div className="quiz-summary-card neutral">
              <span>Attempted</span>
              <strong>{attemptedCount}</strong>
            </div>
            <div className="quiz-summary-card warning">
              <span>Unattempted</span>
              <strong>{unattemptedCount}</strong>
            </div>
            <div className="quiz-summary-card success">
              <span>Correct</span>
              <strong>{correctCount}</strong>
            </div>
            <div className="quiz-summary-card danger">
              <span>Wrong</span>
              <strong>{wrongCount}</strong>
            </div>
            <div className="quiz-summary-card info">
              <span>Score</span>
              <strong>
                {correctCount} / {questions.length}
              </strong>
            </div>
            <div className={`quiz-summary-card ${Number(accuracy) >= 60 ? "success" : "warning"}`}>
              <span>Accuracy</span>
              <strong>{accuracy}%</strong>
            </div>
            <div className={`quiz-summary-card ${scorePercent >= 60 ? "success" : "danger"}`}>
              <span>Total Score %</span>
              <strong>{scorePercent}%</strong>
            </div>
          </div>
          <Link className="quiz-start-btn" to="/daily-current-affairs-quiz">
            Back to Quiz List
          </Link>
        </div>
      </div>
    );
  }

  const displayedTitle = displayedQuiz?.title || quiz.title;
  const timerLabel = formatDuration(remainingSeconds ?? totalQuizSeconds);

  return (
    <div className="quiz-page">
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>

      <div className="quiz-list-card quiz-focus-shell">
        <div>
          <h1>{displayedTitle}</h1>
          <p className="quiz-meta">
            Focus mode is active. The quiz is running in a modal with a live timer.
          </p>
        </div>
        <div className="quiz-focus-stats">
          <div className="quiz-focus-pill">
            <span>Questions</span>
            <strong>{questions.length}</strong>
          </div>
          <div className="quiz-focus-pill warning">
            <span>Time Left</span>
            <strong>{timerLabel}</strong>
          </div>
        </div>
      </div>

      <div className="quiz-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="current-affairs-modal-title">
        <div className="quiz-modal-card quiz-runner-modal">
          <div className="quiz-modal-header">
            <div>
              <p className="quiz-modal-eyebrow">Live Quiz</p>
              <h2 id="current-affairs-modal-title">{displayedTitle}</h2>
            </div>
            <div className={`quiz-timer-chip ${remainingSeconds <= 60 ? "is-danger" : ""}`}>
              <span>Time Left</span>
              <strong>{timerLabel}</strong>
            </div>
          </div>

          {shouldBlockForHindiTranslation ? (
            <div className="quiz-loading-overlay" role="status" aria-live="polite">
              <div className="quiz-loading-spinner" />
              <p>{translationStatus || "Translating quiz to selected language. Please wait..."}</p>
            </div>
          ) : (
            <>
              <div className="quiz-dot-progress" aria-label="Question progress">
                <div className="quiz-progress-track">
                  {questions.map((_, idx) => (
                    <span
                      key={`seg-${idx}`}
                      className={`quiz-progress-segment ${userAnswers[idx] ? "attempted" : ""} ${
                        idx === currentIndex ? "active" : ""
                      }`}
                    />
                  ))}
                </div>
                <span className="quiz-progress-count">
                  {currentIndex + 1}/{questions.length}
                </span>
              </div>

              <div className="quiz-runner-layout">
                <div className="quiz-question-card quiz-question-card-embedded">
                  <div className="question-lang-switch" role="group" aria-label="Question language switch">
                    <button
                      type="button"
                      className={`question-lang-pill ${quizLanguage === "hi" ? "active" : ""}`}
                      onClick={() => handleQuestionLanguageChange("hi")}
                      aria-pressed={quizLanguage === "hi"}
                    >
                      HI
                    </button>
                    <button
                      type="button"
                      className={`question-lang-pill ${quizLanguage === "en" ? "active" : ""}`}
                      onClick={() => handleQuestionLanguageChange("en")}
                      aria-pressed={quizLanguage === "en"}
                    >
                      EN
                    </button>
                  </div>

                  {quizLanguage === "hi" && !isHindiReady ? (
                    <p className="translation-note">
                      {translationStatus || "Translating in background..."}
                    </p>
                  ) : null}

                  <h3>{currentDisplayedQuestion?.question || currentQuestion.question}</h3>

                  <div className="quiz-options">
                    {currentDisplayedQuestion?.options?.map((optionLabel, idx) => (
                      <button
                        type="button"
                        key={`${idx}-${optionLabel}`}
                        className={getOptionClass(idx)}
                        onClick={() => handleSelectOption(idx)}
                        disabled={isSubmitting}
                      >
                        {optionLabel}
                      </button>
                    ))}
                  </div>

                  <div className="quiz-actions">
                    <button
                      type="button"
                      className="quiz-secondary-btn"
                      onClick={handlePrevious}
                      disabled={currentIndex === 0 || isSubmitting}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="quiz-start-btn"
                      onClick={handleNext}
                      disabled={isSubmitting}
                    >
                      {isLastQuestion ? "Next (Last Question)" : "Next Question"}
                    </button>
                    <button
                      type="button"
                      className="quiz-finish-btn"
                      onClick={handleFinish}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Finish Quiz"}
                    </button>
                  </div>

                  {submitError ? <p className="quiz-error">{submitError}</p> : null}

                  {selectedOption && isReviewMode ? (
                    <div className="quiz-explanation">
                      <strong>Description:</strong>{" "}
                      {currentDisplayedQuestion?.details_description ||
                        currentQuestion?.details_description ||
                        "No description available."}
                    </div>
                  ) : null}
                </div>
                <aside className="quiz-solution-nav" aria-label="Question number panel">
                  <div className="quiz-solution-nav-title">Questions</div>
                  <div className="quiz-solution-nav-grid">
                    {questions.map((_, idx) => (
                      <button
                        key={`ca-play-nav-${idx}`}
                        type="button"
                        className={`quiz-solution-index ${getQuestionStatusClass(idx)} ${idx === currentIndex ? "active" : ""}`}
                        onClick={() => setCurrentIndex(idx)}
                        disabled={isSubmitting}
                      >
                        {idx + 1}
                      </button>
                    ))}
                  </div>
                </aside>
              </div>
            </>
          )}
        </div>
      </div>

      {isFinishModalOpen ? (
        <div
          className="quiz-confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="current-affairs-submit-title"
        >
          <div className="quiz-confirm-card">
            <p className="quiz-modal-eyebrow">Confirm Submission</p>
            <h2 id="current-affairs-submit-title">Submit quiz now?</h2>
            <p className="quiz-meta">
              You have attempted {attemptedCount} of {questions.length} questions. If this was a
              mistake, you can continue the quiz.
            </p>
            <div className="quiz-actions">
              <button
                type="button"
                className="quiz-secondary-btn"
                onClick={() => setIsFinishModalOpen(false)}
                disabled={isSubmitting}
              >
                Continue Quiz
              </button>
              <button
                type="button"
                className="quiz-finish-btn"
                onClick={submitQuizAttempt}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Quiz"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {isWrapConfirmOpen ? (
        <div
          className="quiz-confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="current-affairs-wrap-title"
        >
          <div className="quiz-confirm-card">
            <p className="quiz-modal-eyebrow">Last Question</p>
            <h2 id="current-affairs-wrap-title">Move to first question?</h2>
            <p className="quiz-meta">
              You are on the last question. Do you want to go back to question 1?
            </p>
            <div className="quiz-actions">
              <button
                type="button"
                className="quiz-secondary-btn"
                onClick={() => setIsWrapConfirmOpen(false)}
                disabled={isSubmitting}
              >
                Stay Here
              </button>
              <button
                type="button"
                className="quiz-start-btn"
                onClick={() => {
                  setCurrentIndex(0);
                  setIsWrapConfirmOpen(false);
                }}
                disabled={isSubmitting}
              >
                Go To First
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function CurrentAffairsQuizLeaderboardPage() {
  const { slugWithId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const listPath = "/daily-current-affairs-quiz";
  const startPath = `/daily-current-affairs-quiz/${slugWithId}/start?lang=hi`;
  const canonicalUrl = buildCanonicalUrl(
    `/daily-current-affairs-quiz/${encodeURIComponent(slugWithId || "")}/leaderboard`
  );
  const [quizTitle, setQuizTitle] = useState("Daily Current Affairs Leaderboard");
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [solutionPayload, setSolutionPayload] = useState(null);
  const [solutionQuestions, setSolutionQuestions] = useState([]);
  const [isSolutionOpen, setIsSolutionOpen] = useState(false);
  const [solutionIndex, setSolutionIndex] = useState(0);
  const [solutionLanguage, setSolutionLanguage] = useState("en");
  const [solutionHindiQuestions, setSolutionHindiQuestions] = useState([]);
  const [isSolutionHindiReady, setIsSolutionHindiReady] = useState(true);
  const [solutionTranslationStatus, setSolutionTranslationStatus] = useState("");
  const [activeReportTab, setActiveReportTab] = useState("stats");
  const currentUserId = getUser()?.id;
  const attemptCacheKey = useMemo(
    () => makeCurrentAffairsAttemptKey(slugWithId),
    [slugWithId]
  );

  useEffect(() => {
    if (isAuthenticated()) return;
    redirectToLoginForCurrentAffairsQuiz(
      navigate,
      `${location.pathname}${location.search}`
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    let mounted = true;

    const loadQuizTitle = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/current-affairs-quizzes/${slugWithId}`
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!mounted) return;
        setQuizTitle(data?.title || "Daily Current Affairs Leaderboard");
      } catch {
        // Ignore metadata errors.
      }
    };

    loadQuizTitle();
    return () => {
      mounted = false;
    };
  }, [slugWithId]);

  const loadLeaderboard = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    setError("");

    try {
      const data = await fetchCurrentAffairsLeaderboard(slugWithId);
      setLeaderboard(data);
    } catch (err) {
      setError(err.message || "Unable to load leaderboard. Please try again.");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [slugWithId]);

  useEffect(() => {
    if (!isAuthenticated()) return;
    loadLeaderboard();
  }, [loadLeaderboard]);

  useEffect(() => {
    if (!isAuthenticated()) return;
    setSolutionPayload(readAttemptSolutions(attemptCacheKey));
  }, [attemptCacheKey]);

  useEffect(() => {
    if (!isAuthenticated() || !slugWithId) {
      setUserStats(null);
      return undefined;
    }

    let mounted = true;

    const loadUserStats = async () => {
      try {
        const response = await apiFetch(
          `/api/current-affairs-quizzes/${encodeURIComponent(slugWithId)}/my-stats`,
          { auth: true }
        );
        const data = await readJsonSafe(response);
        if (!mounted) return;
        if (!response.ok) {
          setUserStats(null);
          return;
        }
        setUserStats(normalizeCurrentAffairsStats(data));
      } catch {
        if (!mounted) return;
        setUserStats(null);
      }
    };

    loadUserStats();
    return () => {
      mounted = false;
    };
  }, [slugWithId]);

  useEffect(() => {
    if (!isAuthenticated() || !slugWithId) return undefined;
    let mounted = true;
    const loadQuestions = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/current-affairs-quizzes/${slugWithId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!mounted) return;
        setSolutionQuestions(Array.isArray(data?.questions) ? data.questions : []);
      } catch {
        if (!mounted) return;
        setSolutionQuestions([]);
      }
    };
    loadQuestions();
    return () => {
      mounted = false;
    };
  }, [slugWithId]);

  useEffect(() => {
    let mounted = true;

    const buildHindiSolutions = async () => {
      if (solutionLanguage !== "hi") {
        if (!mounted) return;
        setIsSolutionHindiReady(true);
        setSolutionTranslationStatus("");
        return;
      }
      if (!solutionQuestions.length) {
        if (!mounted) return;
        setSolutionHindiQuestions([]);
        setIsSolutionHindiReady(true);
        setSolutionTranslationStatus("");
        return;
      }

      setIsSolutionHindiReady(false);
      setSolutionTranslationStatus("Translating solutions...");

      try {
        const translated = await mapWithConcurrency(solutionQuestions, 3, async (q) => {
          const sourceLanguage = getQuestionSourceLanguage(q);
          const [questionText, descriptionText, translatedOptions] = await Promise.all([
            translateQuestionToHindi(q.question, "hi", sourceLanguage),
            translateText(q.details_description, "hi", sourceLanguage),
            Promise.all((q.options || []).map((option) => translateOptionToHindi(option, "hi", sourceLanguage))),
          ]);
          return {
            ...q,
            question: questionText || q.question,
            details_description: descriptionText || q.details_description,
            options: translatedOptions.length ? translatedOptions : q.options,
          };
        });
        if (!mounted) return;
        setSolutionHindiQuestions(translated);
        setIsSolutionHindiReady(true);
        setSolutionTranslationStatus("");
      } catch {
        if (!mounted) return;
        setSolutionHindiQuestions(solutionQuestions);
        setIsSolutionHindiReady(true);
        setSolutionTranslationStatus("");
      }
    };

    buildHindiSolutions();
    return () => {
      mounted = false;
    };
  }, [solutionLanguage, solutionQuestions]);

  const entries = Array.isArray(leaderboard?.entries) ? leaderboard.entries : [];
  const title = leaderboard?.quizTitle || quizTitle;
  const currentUserEntry = entries.find(
    (entry) =>
      entry?.isCurrentUser ||
      String(entry?.user?.userId ?? entry?.user?.id ?? "") === String(currentUserId ?? "")
  );
  const canOpenSolution =
    Array.isArray(solutionPayload?.answers) &&
    solutionPayload.answers.length > 0 &&
    solutionQuestions.length > 0;
  const displayedSolutionQuestions =
    solutionLanguage === "hi"
      ? isSolutionHindiReady
        ? solutionHindiQuestions
        : []
      : solutionQuestions;
  const activeSolutionQuestion = displayedSolutionQuestions[solutionIndex] || null;
  const canonicalSolutionQuestion = solutionQuestions[solutionIndex] || null;
  const totalSolutionQuestions = displayedSolutionQuestions.length;
  const difficultyStats = useMemo(() => {
    const buckets = {
      easy: { total: 0, attempted: 0, correct: 0, wrong: 0 },
      medium: { total: 0, attempted: 0, correct: 0, wrong: 0 },
      hard: { total: 0, attempted: 0, correct: 0, wrong: 0 },
    };
    solutionQuestions.forEach((question, idx) => {
      const key = normalizeDifficultyLabel(question?.difficulty || question?.level);
      buckets[key].total += 1;
      const selected = solutionPayload?.answers?.[idx]?.selectedOption || "";
      if (!selected) return;
      buckets[key].attempted += 1;
      if (areOptionsEqual(selected, question?.correct_option)) {
        buckets[key].correct += 1;
      } else {
        buckets[key].wrong += 1;
      }
    });
    return buckets;
  }, [solutionPayload?.answers, solutionQuestions]);
  const derivedCorrect = solutionQuestions.reduce((count, question, idx) => {
    const selected = solutionPayload?.answers?.[idx]?.selectedOption || "";
    if (!selected) return count;
    return areOptionsEqual(selected, question?.correct_option) ? count + 1 : count;
  }, 0);
  const derivedAttempted = (solutionPayload?.answers || []).filter((item) => item?.selectedOption).length;
  const totalQuestions = solutionQuestions.length;
  const effectiveStats =
    normalizeCurrentAffairsStats(currentUserEntry, userStats || {}) ||
    normalizeCurrentAffairsStats(userStats, {
      totalQuestions,
      attemptedQuestions: derivedAttempted,
      correctAnswers: derivedCorrect,
      wrongAnswers: Math.max(derivedAttempted - derivedCorrect, 0),
      score: derivedCorrect,
    }) ||
    normalizeCurrentAffairsStats({
      totalQuestions,
      attemptedQuestions: derivedAttempted,
      correctAnswers: derivedCorrect,
      wrongAnswers: Math.max(derivedAttempted - derivedCorrect, 0),
      score: derivedCorrect,
    });
  const statsScore = Number(effectiveStats?.score || 0);
  const statsTotalMarks = Number(effectiveStats?.totalMarks ?? totalQuestions ?? 0);
  const statsAttempted = Number(effectiveStats?.attemptedQuestions ?? derivedAttempted ?? 0);
  const statsCorrect = Number(effectiveStats?.correctAnswers ?? derivedCorrect ?? 0);
  const statsWrong = Number(
    effectiveStats?.wrongAnswers ?? Math.max(statsAttempted - statsCorrect, 0)
  );
  const statsAccuracy = Number(
    effectiveStats?.accuracy ??
      (statsAttempted ? (statsCorrect / statsAttempted) * 100 : 0)
  );

  if (loading) {
    return (
      <div className="quiz-page">
        <div className="quiz-list-card">
          <div className="quiz-skeleton-line short" />
          <div className="quiz-skeleton-line medium" />
          <div className="quiz-skeleton-line long" />
        </div>
        <div className="quiz-list-card quiz-leaderboard-table-wrap">
          <div className="quiz-skeleton-row" />
          <div className="quiz-skeleton-row" />
          <div className="quiz-skeleton-row" />
          <div className="quiz-skeleton-row" />
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-page">
      <Helmet>
        <title>{`${title} Leaderboard | AAMSchool`}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta
          name="description"
          content={`Review leaderboard and performance for ${title} on AAMSchool.`}
        />
        <meta name="robots" content="noindex, nofollow" />
        <meta property="og:title" content={`${title} Leaderboard | AAMSchool`} />
        <meta
          property="og:description"
          content={`Review leaderboard and performance for ${title} on AAMSchool.`}
        />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${title} Leaderboard | AAMSchool`} />
        <meta
          name="twitter:description"
          content={`Review leaderboard and performance for ${title} on AAMSchool.`}
        />
      </Helmet>
      <div className="quiz-list-card quiz-leaderboard-header">
        <div>
          <h1>{title}</h1>
          <p className="quiz-meta">Daily Current Affairs Quiz</p>
          <p className="quiz-leaderboard-meta">
            Participants: <strong>{leaderboard?.totalParticipants || 0}</strong>
            {" | "}
            Last updated: <strong>{formatStatsDateTime(leaderboard?.generatedAt)}</strong>
          </p>
        </div>
        <div className="quiz-leaderboard-actions">
          <button
            type="button"
            className="quiz-secondary-btn"
            onClick={() => loadLeaderboard({ silent: true })}
            disabled={isRefreshing}
          >
            {isRefreshing ? "Refreshing..." : "Refresh"}
          </button>
          <button type="button" className="quiz-start-btn" onClick={() => navigate(startPath)}>
            Retake Quiz
          </button>
          <button
            type="button"
            className="quiz-secondary-btn"
            onClick={() => {
              setSolutionIndex(0);
              setSolutionLanguage("en");
              setIsSolutionOpen(true);
            }}
            disabled={!canOpenSolution}
          >
            Solution
          </button>
          <button type="button" className="quiz-secondary-btn" onClick={() => navigate(listPath)}>
            Back To Quiz List
          </button>
        </div>
      </div>

      {error ? (
        <div className="quiz-list-card">
          <p className="quiz-error">{error}</p>
          <div className="quiz-actions">
            <button type="button" className="quiz-start-btn" onClick={() => loadLeaderboard()}>
              Retry
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="quiz-list-card quiz-report-tabs">
            <button
              type="button"
              className={`quiz-report-tab ${activeReportTab === "stats" ? "active" : ""}`}
              onClick={() => setActiveReportTab("stats")}
            >
              Quiz Stats
            </button>
            <button
              type="button"
              className={`quiz-report-tab ${activeReportTab === "leaderboard" ? "active" : ""}`}
              onClick={() => setActiveReportTab("leaderboard")}
            >
              Leaderboard
            </button>
          </div>

          {activeReportTab === "leaderboard" ? (
            entries.length === 0 ? (
              <div className="quiz-list-card quiz-empty-state">
                <p>No attempts yet for this quiz.</p>
              </div>
            ) : (
              <div className="quiz-list-card quiz-leaderboard-table-wrap">
                <table className="quiz-leaderboard-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>User</th>
                      <th>Score</th>
                      <th>Accuracy</th>
                      <th>Correct / Wrong</th>
                      <th>Time Taken</th>
                      <th>Attempted</th>
                      <th>Submitted At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry, index) => (
                      <tr
                        key={`${entry?.user?.userId || entry?.user?.id || "user"}-${entry?.submittedAt || index}`}
                        className={entry?.isCurrentUser ? "current-user" : ""}
                      >
                        <td data-label="Rank">{`#${entry?.rank || index + 1}`}</td>
                        <td data-label="User">
                          <span>{entry?.user?.username || entry?.user?.name || "User"}</span>
                          {entry?.isCurrentUser ? <span className="quiz-you-badge">You</span> : null}
                        </td>
                        <td data-label="Score">{`${entry?.score || 0} / ${entry?.totalMarks || 0}`}</td>
                        <td data-label="Accuracy">{formatPercent(entry?.accuracy)}</td>
                        <td data-label="Correct / Wrong">{`${entry?.correctAnswers || 0} / ${entry?.wrongAnswers || 0}`}</td>
                        <td data-label="Time Taken">{formatDuration(entry?.timeTakenSeconds)}</td>
                        <td data-label="Attempted">{entry?.attemptedQuestions || 0}</td>
                        <td data-label="Submitted At">{formatStatsDateTime(entry?.submittedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <div className="quiz-list-card quiz-stats-shell">
              <div className="quiz-stats-grid">
                <div className="quiz-summary-card info">
                  <span>Score</span>
                  <strong>{`${statsScore} / ${statsTotalMarks}`}</strong>
                </div>
                <div className="quiz-summary-card neutral">
                  <span>Attempted</span>
                  <strong>{statsAttempted}</strong>
                </div>
                <div className="quiz-summary-card success">
                  <span>Correct</span>
                  <strong>{statsCorrect}</strong>
                </div>
                <div className="quiz-summary-card danger">
                  <span>Wrong</span>
                  <strong>{statsWrong}</strong>
                </div>
                <div className="quiz-summary-card warning">
                  <span>Accuracy</span>
                  <strong>{`${statsAccuracy.toFixed(0)}%`}</strong>
                </div>
                <div className="quiz-summary-card neutral">
                  <span>Time Taken</span>
                  <strong>{formatDuration(effectiveStats?.timeTakenSeconds || 0)}</strong>
                </div>
              </div>
              <div className="quiz-difficulty-chart">
                {["easy", "medium", "hard"].map((level) => {
                  const bucket = difficultyStats[level];
                  const attemptedPct = bucket.total ? (bucket.attempted / bucket.total) * 100 : 0;
                  const correctPct = bucket.total ? (bucket.correct / bucket.total) * 100 : 0;
                  return (
                    <div className="quiz-difficulty-row" key={`diff-${level}`}>
                      <div className="quiz-difficulty-label">{level.toUpperCase()}</div>
                      <div className="quiz-difficulty-bars">
                        <div className="quiz-diff-track">
                          <span className="quiz-diff-fill attempted" style={{ width: `${attemptedPct}%` }} />
                        </div>
                        <div className="quiz-diff-track">
                          <span className="quiz-diff-fill correct" style={{ width: `${correctPct}%` }} />
                        </div>
                      </div>
                      <div className="quiz-difficulty-values">
                        <span>{`${bucket.correct}/${bucket.total} correct`}</span>
                        <span>{`${bucket.attempted} attempted`}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
      {isSolutionOpen ? (
        <div className="quiz-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="ca-solution-title">
          <div className="quiz-confirm-card quiz-solution-card">
            <p className="quiz-modal-eyebrow">Attempt Solution</p>
            <h2 id="ca-solution-title">{title}</h2>
            <div className="question-lang-switch" role="group" aria-label="Solution language switch">
              <button
                type="button"
                className={`question-lang-pill ${solutionLanguage === "hi" ? "active" : ""}`}
                onClick={() => setSolutionLanguage("hi")}
                aria-pressed={solutionLanguage === "hi"}
              >
                HI
              </button>
              <button
                type="button"
                className={`question-lang-pill ${solutionLanguage === "en" ? "active" : ""}`}
                onClick={() => setSolutionLanguage("en")}
                aria-pressed={solutionLanguage === "en"}
              >
                EN
              </button>
            </div>
            {solutionLanguage === "hi" && !isSolutionHindiReady ? (
              <p className="translation-note">{solutionTranslationStatus || "Translating solutions..."}</p>
            ) : null}
            <div className="quiz-solution-layout">
              <div className="quiz-solution-main">
                {activeSolutionQuestion ? (
                  <div className="quiz-solution-item">
                    <h3>{`Q${solutionIndex + 1}. ${activeSolutionQuestion?.question || "Question"}`}</h3>
                    <div className="quiz-options">
                      {(activeSolutionQuestion?.options || []).map((optionText, optionIndex) => {
                        const selected = solutionPayload?.answers?.[solutionIndex]?.selectedOption || "";
                        const correct = canonicalSolutionQuestion?.correct_option || "";
                        const canonicalOption = canonicalSolutionQuestion?.options?.[optionIndex] || optionText;
                        const isCorrect = areOptionsEqual(canonicalOption, correct);
                        const isSelected = areOptionsEqual(canonicalOption, selected);
                        const optionClass = isCorrect
                          ? "quiz-option correct"
                          : isSelected
                          ? "quiz-option wrong"
                          : "quiz-option";
                        return (
                          <button
                            key={`ca-sol-opt-${solutionIndex}-${optionIndex}`}
                            type="button"
                            className={optionClass}
                            disabled
                          >
                            {optionText}
                            {isSelected && isCorrect ? <span className="quiz-option-note right">You got it right</span> : null}
                            {isSelected && !isCorrect ? <span className="quiz-option-note wrong">Your answer</span> : null}
                            {!isSelected && isCorrect ? <span className="quiz-option-note right">Correct answer</span> : null}
                          </button>
                        );
                      })}
                    </div>
                    <div className="quiz-explanation">
                      <strong>Description:</strong>{" "}
                      {activeSolutionQuestion?.details_description || "No description available."}
                    </div>
                  </div>
                ) : null}
              </div>
              <aside className="quiz-solution-nav" aria-label="Question number panel">
                <div className="quiz-solution-nav-title">Questions</div>
                <div className="quiz-solution-nav-grid">
                  {solutionQuestions.map((question, idx) => {
                    const selected = solutionPayload?.answers?.[idx]?.selectedOption || "";
                    const correct = question?.correct_option || "";
                    const statusClass = !selected
                      ? "unattempted"
                      : areOptionsEqual(selected, correct)
                      ? "correct"
                      : "wrong";
                    return (
                      <button
                        key={`ca-sol-nav-${idx}`}
                        type="button"
                        className={`quiz-solution-index ${statusClass} ${idx === solutionIndex ? "active" : ""}`}
                        onClick={() => setSolutionIndex(idx)}
                      >
                        {idx + 1}
                      </button>
                    );
                  })}
                </div>
              </aside>
            </div>
            <div className="quiz-actions">
              <button
                type="button"
                className="quiz-secondary-btn"
                onClick={() => setSolutionIndex((prev) => Math.max(prev - 1, 0))}
                disabled={solutionIndex === 0}
              >
                Previous
              </button>
              <button
                type="button"
                className="quiz-start-btn"
                onClick={() => setSolutionIndex((prev) => Math.min(prev + 1, totalSolutionQuestions - 1))}
                disabled={solutionIndex >= totalSolutionQuestions - 1}
              >
                Next
              </button>
              <button type="button" className="quiz-secondary-btn" onClick={() => setIsSolutionOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}


